import { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import api from '../../api/axios';

interface DatasetSummary {
  dataset_id: string;
  display_name: string;
  status: string;
  data_source_type: string;
  row_count: number | null;
  last_refreshed_at: string | null;
}

interface Props {
  selectedId?: string;
  onSelect: (id: string) => void;
}

const STATUS_OPTIONS = ['all', 'available', 'in_progress', 'proposed'];

const DatasetList = ({ selectedId, onSelect }: Props) => {
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: Record<string, string> = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    api
      .get('/explore/datasets/', { params })
      .then((res) => { if (!cancelled) setDatasets(res.data.datasets || []); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter]);

  return (
    <Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Typography variant="overline" color="text.secondary">Datasets</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {loading ? 'loading…' : `${datasets.length}`}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as string)} sx={{ minWidth: 130 }}>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
      {error && <Box sx={{ p: 2 }}><Typography color="error" variant="body2">{error}</Typography></Box>}
      {!loading && !error && datasets.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No datasets registered.</Typography>
        </Box>
      )}
      {datasets.map((d) => {
        const active = d.dataset_id === selectedId;
        return (
          <Box
            key={d.dataset_id}
            onClick={() => onSelect(d.dataset_id)}
            sx={{
              px: 2, py: 1.5,
              borderBottom: 1, borderColor: 'divider',
              cursor: 'pointer',
              backgroundColor: active ? 'rgba(58, 68, 82, 0.08)' : 'transparent',
              borderLeft: active ? '3px solid' : '3px solid transparent',
              borderLeftColor: active ? 'primary.main' : 'transparent',
              '&:hover': { backgroundColor: active ? 'rgba(58, 68, 82, 0.12)' : 'rgba(0,0,0,0.03)' },
              transition: 'background-color 0.12s',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{d.display_name || d.dataset_id}</Typography>
              <Chip label={d.status} size="small" color={datasetStatusColor(d.status)} sx={{ height: 18, fontSize: '0.65rem' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontFamily: 'monospace' }}>
              {d.dataset_id}
            </Typography>
            {d.row_count !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {d.row_count.toLocaleString()} rows · {d.data_source_type}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

function datasetStatusColor(s: string): 'default' | 'success' | 'warning' | 'info' {
  switch (s) {
    case 'available': return 'success';
    case 'in_progress': return 'info';
    case 'proposed': return 'warning';
    default: return 'default';
  }
}

export default DatasetList;
